import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from './Navbar';
import RestaurantHeader from '../restaurant/RestaurantHeader';
import RestaurantInfo from '../restaurant/RestaurantInfo';
import { usePlaceData } from '../restaurant/UseRestaurantData';
import ReactMarkdown from 'react-markdown';
import Footer from './Footer';
import ReviewsSection from '../restaurant/ReveiwSection';
import ImageGallery from './ImageGallery';
import WriteComment from './WriteComment';
import MapComponent from '../restaurant/MapComponent';

export interface Review {
  review_id: string;
  user_id: string | null;
  rate: number;
  comment: string;
  date: string;
}

export interface Image {
  image_id: string;
  filename: string;
  description?: string | null;
}

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// interface ImageGalleryProps {
//   images: Image[];
//   restaurantName: string;
//   baseUrl?: string;
// }

// const ImageGallery: React.FC<ImageGalleryProps> = ({ images, restaurantName, baseUrl }) => {
//   return (
//     <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 my-6">
//       {images.map((img) => (
//         <img
//           key={img.image_id}
//           src={`${baseUrl}/images/${img.image_id}`}
//           alt={img.description || restaurantName}
//           className="w-full h-64 object-cover rounded-lg shadow-md"
//           loading="lazy"
//         />
//       ))}
//     </div>
//   );
// };

const PlaceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { restaurant, loading, error } = usePlaceData(id);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const writeCommentRef = useRef<HTMLDivElement | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const reviewsPerPage = 4;

  const scrollToWriteComment = () => {
    writeCommentRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (!id) return;

    fetch(`${BASE_URL}/reviews/place/${id}`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch reviews");
        return res.json();
      })
      .then((data) => setReviews(data))
      .catch((err) => {
        console.error(err);
        setReviewError("خطا در دریافت نظرات کاربران.");
      });
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-lootka-green"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !restaurant) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-myYekanDemibold text-gray-900">مکان مورد نظر پیدا نشد.</h1>
          <p className="text-gray-600 mt-2">{error}</p>
          <Link to="/restaurant" className="text-lootka-green font-myYekanRegular hover:underline mt-4 inline-block">
            بازگشت
          </Link>
        </div>
      </div>
    );
  }

  // Pagination logic
  const indexOfLastReview = currentPage * reviewsPerPage;
  const indexOfFirstReview = indexOfLastReview - reviewsPerPage;
  const currentReviews = reviews.slice(indexOfFirstReview, indexOfLastReview);
  const totalPages = Math.ceil(reviews.length / reviewsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="min-h-screen bg-white px-[24px] xl:px-0">
      <Navbar />

      <div className="pt-12 mt-16">
        {/* 12-column grid with margin 80px and gutter 24px */}
        <div className="w-full max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-x-6">

          {/* Header and Tabs (full width) */}
          <div className="col-span-12">
            <RestaurantHeader restaurant={restaurant} onWriteCommentClick={scrollToWriteComment} />

            <ImageGallery
              images={restaurant.images || []}
              restaurantName={restaurant.name}
              baseUrl={BASE_URL}
            />
            {/* <NavigationTabs activeTab={activeTab} setActiveTab={setActiveTab} /> */}
          </div>

          {/* About + Info side by side */}
          <div className="col-span-12 md:col-span-8">
            <div className="mb-6">
              <h2 className="desktop:text-2xl tablet:text-xl text-[20px] font-myYekanDemibold mb-2">
                {restaurant.sub_category === "رستوران" && "درباره رستوران"}
                {restaurant.sub_category === "اقامتگاه" && "درباره اقامتگاه"}
                {restaurant.sub_category === "کافه" && "درباره کافه"}
                {restaurant.sub_category === "جای دیدنی" && "درباره جای دیدنی"}
              </h2>
              <p className="text-gray-700 text-justify leading-relaxed font-myYekanFaNumRegular bg-[#F6FDFE] p-[16px] rounded-[12px]">
                <ReactMarkdown>{restaurant.description}</ReactMarkdown>
              </p>
            </div>
          </div>
          <div className="col-span-12 md:col-span-4">
            <RestaurantInfo restaurant={restaurant} />
          </div>


          {/* Reviews + WriteComment + Map side by side */}
          <div className="col-span-12 mt-6 grid grid-cols-12 gap-6">
            {/* نوشتن نظر + نظرات کاربران */}
            <div className="col-span-12 md:col-span-8 space-y-6">
              {/* نوشتن نظر */}
              <div ref={writeCommentRef}>
                <span className="font-myYekanDemibold text-[20px]">
                  به {restaurant.name} امتیاز دهید:
                </span>
                <WriteComment />
              </div>

              {/* نظرات کاربران */}
              <ReviewsSection
                reviewSummary={restaurant.review_summary}
                reviews={currentReviews}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>

            {/* نقشه */}
            <div className="col-span-12 md:col-span-4">
              <MapComponent
                latitude={restaurant.latitude}
                longitude={restaurant.longitude}
                name={restaurant.name}
              />
            </div>
          </div>

        </div>
      </div>
      {/* 📱 Mobile Bottom Action Bar */}
      <div className="fixed gap-2 bottom-0 left-0 w-full bg-white border-t border-gray-200 shadow-md flex justify-between items-center px-6 py-3 md:hidden z-[9999]">
        {(() => {
          const googleMapsLink =
            restaurant.latitude && restaurant.longitude
              ? `https://www.google.com/maps?q=${restaurant.latitude},${restaurant.longitude}`
              : restaurant.address
                ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  restaurant.address
                )}`
                : null;

          return googleMapsLink ? (
            <a
              href={googleMapsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 mr-2 border border-[#4793A8] text-[#4793A8] text-center py-3 rounded-xl font-myYekanDemibold text-[16px]"
            >
              مسیریابی
            </a>
          ) : (
            <button
              disabled
              className="flex-1 mr-2 bg-gray-300 text-gray-500 text-center py-3 rounded-xl font-myYekanDemibold text-[16px] cursor-not-allowed"
            >
              مسیریابی
            </button>
          );
        })()}

        {/* ☎️ دکمه تماس */}
        {restaurant.phone ? (
          <a
            href={`tel:${restaurant.phone}`}
            className="flex-1 ml-2 bg-[#4793A8] text-white text-center py-3 rounded-xl font-myYekanDemibold text-[16px]"
          >
            تماس
          </a>
        ) : (
          <button
            disabled
            className="flex-1 ml-2 bg-gray-300 text-gray-500 text-center py-3 rounded-xl font-myYekanDemibold text-[16px] cursor-not-allowed"
          >
            تماس
          </button>
        )}
      </div>


      <Footer />
    </div>
  );
};

export default PlaceDetail;
